import {i18n} from '@online-offer/i18n';
import {Hide, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {always, isNotNil} from 'ramda';

import {Nullish, parseDate} from 'shared/web';

import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ReservationCard} from '../ReservationCard/ReservationCard';
import {SellerCard} from '../SellerCard/SellerCard';
import {TimerCard, TimerCardColorSchema, TimerState, TimerStateType} from '../TimerCard/TimerCard';

const getTimerState = ({
  days,
  hours,
  minutes,
  seconds,
}: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}): TimerState => {
  if (days === 0 && hours < 2) {
    if (hours === 0 && minutes === 0 && seconds === 0) {
      return 'expired';
    } else {
      return 'soon_expired';
    }
  }
  return 'reserved';
};

const getTime = (deadline: string | Nullish) => {
  if (!deadline) {
    return null;
  }
  const time = parseDate(deadline).getTime() - new Date().getTime();
  if (time <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }
  return {
    days: Math.floor(time / (1000 * 60 * 60 * 24)),
    hours: Math.floor((time / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((time / 1000 / 60) % 60),
    seconds: Math.floor((time / 1000) % 60),
  };
};

const getReservationColorSchema = (timerState: TimerState): TimerCardColorSchema =>
  match<TimerState, TimerCardColorSchema>(timerState)
    .with('reserved', always({cardBg: 'semantic.bg.green', textColor: 'successDark'}))
    .with('soon_expired', always({cardBg: 'severity.warningCard', textColor: 'warningDark'}))
    .with('expired', always({cardBg: 'severity.dangerCard', textColor: 'primary'}))
    .exhaustive();

const OFFER_VALIDITY_COLOR_SCHEMA: TimerCardColorSchema = {
  cardBg: 'accent.lightest',
  textColor: 'accentDarker',
};

export function Sidebar() {
  const {activeReservation, offerValidTo} = useGetOnlineOfferData();

  const initialReservationTime = getTime(activeReservation?.reservedUntil);
  const [reservationTimer, setReservationTimer] = useState<TimerStateType | null>(
    initialReservationTime
      ? {...initialReservationTime, state: getTimerState(initialReservationTime)}
      : null
  );

  const initialOfferValidityTime = getTime(offerValidTo);
  const [offerValidityTimer, setOfferValidityTimer] = useState<TimerStateType | null>(
    initialOfferValidityTime
      ? {...initialOfferValidityTime, state: getTimerState(initialOfferValidityTime)}
      : null
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const time = getTime(activeReservation?.reservedUntil);
      if (!time) {
        return;
      }
      const timerState = getTimerState(time);
      setReservationTimer({...time, state: timerState});
      if (time.days === 0 && time.hours === 0 && time.minutes === 0 && time.seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [activeReservation?.reservedUntil]);

  useEffect(() => {
    const interval = setInterval(() => {
      const time = getTime(offerValidTo);
      if (!time) {
        return;
      }
      const timerState = getTimerState(time);
      setOfferValidityTimer({...time, state: timerState});
      if (time.days === 0 && time.hours === 0 && time.minutes === 0 && time.seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [offerValidTo]);

  const reservationColorSchema = isNotNil(reservationTimer)
    ? getReservationColorSchema(reservationTimer.state)
    : undefined;

  return (
    <>
      <VStack spacing={2}>
        <Show when={isNotNil(offerValidityTimer) && offerValidityTimer?.state !== 'expired'}>
          <TimerCard
            timer={offerValidityTimer!}
            reservedUntil={offerValidTo ?? ''}
            colorSchema={OFFER_VALIDITY_COLOR_SCHEMA}
            label={i18n.t('navigation.offerValidity')}
          />
        </Show>
        <Show when={isNotNil(reservationTimer) && isNotNil(reservationColorSchema)}>
          <TimerCard
            timer={reservationTimer!}
            reservedUntil={activeReservation?.reservedUntil ?? ''}
            colorSchema={reservationColorSchema!}
          />
        </Show>
      </VStack>
      <ReservationCard isExpired={reservationTimer?.state === 'expired'} />
      <Hide onMobile onTablet>
        <SellerCard />
      </Hide>
    </>
  );
}
